import { Form, FormField } from "src/Components/ui/form";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import FormFieldItem from "src/common/form/FormFieldItem";
import { Input } from "src/Components/ui/input";
import { Button } from "src/Components/ui/button";
import { DialogClose } from "src/Components/ui/dialog";
import { socialMediaTypes, SocialMediaIcons } from "src/enums/socialMediaType";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "src/Components/ui/card";
import { Share2, Link2, Tag, Globe } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/Components/ui/select";
import { useController } from "react-hook-form";
import { indexStore } from "src/store/indexStore";

function SocialMediaForm(props) {
  const { t } = useTranslation();

  const dialogCloseRef = useRef(null);

  const formSchema = z.object({
    link: z
      .string({
        required_error: `${t("gloabl.url")} ${t(
          "errorMessage.fieldRequired"
        )}.`,
      })
      .startsWith("https://", {
        message: `${t("gloabl.url")} ${t("errorMessage.startwith")} https://.`,
      }),

    title: z
      .string({
        required_error: `${t("gloabl.name")} ${t(
          "errorMessage.fieldRequired"
        )}.`,
      })
      .min(2, {
        message: `${t("gloabl.name")} ${t("errorMessage.fieldCharsLength")}.`,
      }),
    type: z.any({
      required_error: `${t("gloabl.type")} ${t("errorMessage.fieldRequired")}.`,
    }),
  });

  // 1. Define your form.
  const form = useForm({
    resolver: zodResolver(formSchema),

    defaultValues: {
      ...props.datasource,
    },
  });

  const onSubmit = (values) => {
    
    props.onSubmit(values);
    if (dialogCloseRef.current) {
      dialogCloseRef.current.click();
    }
  };
  const onErorr = (errors) => {
    toast.error(t("errorMessage.fillAll"));
  };
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit, onErorr)}
        className="space-y-6"
      >
        {/* Basic Information */}
        <Card className="border-2">
          <CardHeader className="flex-row items-center gap-3 pb-3 border-b border-blue-200 dark:border-blue-900">
            <div className="p-2 rounded-lg bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
              <Share2 className="w-5 h-5" />
            </div>
            <div>
              <CardTitle className="text-lg">{t("gloabl.social_media_info") || "Social Media Information"}</CardTitle>
              <CardDescription>
                {t("gloabl.social_media_info_desc") || "Add your social media platform details"}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => {
                const { lang } = indexStore();
                const selectedPlatform = socialMediaTypes.find((p) => p.id == field.value);
                const SelectedIcon = selectedPlatform?.icon;

                return (
                  <FormFieldItem
                    required
                    form={form}
                    label={
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        <span>{t("gloabl.platform") || t("gloabl.type")}</span>
                      </div>
                    }
                    description={t("gloabl.select_platform") || "Choose the social media platform"}
                  >
                    <Select
                      dir={lang === "ar" ? "rtl" : "ltr"}
                      value={field.value ? String(field.value) : undefined}
                      onValueChange={(value) => field.onChange(Number(value))}
                    >
                      <SelectTrigger className="w-full h-12">
                        <div className="flex items-center gap-3 flex-1">
                          {SelectedIcon && (
                            <SelectedIcon className="w-6 h-6 text-primary shrink-0" />
                          )}
                          <SelectValue placeholder={t("gloabl.select") + " " + (t("gloabl.platform") || t("gloabl.type"))}>
                            {selectedPlatform ? (
                              <span className="flex items-center gap-2">
                                {selectedPlatform.name}
                              </span>
                            ) : null}
                          </SelectValue>
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {socialMediaTypes.map((platform) => {
                            const IconComponent = platform.icon;
                            return (
                              <SelectItem
                                key={platform.id}
                                value={String(platform.id)}
                                className="py-3"
                              >
                                <div className="flex items-center gap-3 w-full">
                                  <IconComponent className="w-6 h-6 text-primary shrink-0" />
                                  <span className="font-medium">{platform.name}</span>
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </FormFieldItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormFieldItem
                  required
                  form={form}
                  label={
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      <span>{t("gloabl.title") || t("gloabl.name")}</span>
                    </div>
                  }
                  description={t("gloabl.social_media_title_desc") || "Enter a name for this social media link"}
                >
                  <Input {...field} placeholder={t("gloabl.social_media_title_placeholder") || "e.g., Facebook Page"} />
                </FormFieldItem>
              )}
            />
            <FormField
              control={form.control}
              name="link"
              render={({ field }) => (
                <FormFieldItem
                  required
                  form={form}
                  label={
                    <div className="flex items-center gap-2">
                      <Link2 className="w-4 h-4" />
                      <span>{t("gloabl.url")}</span>
                    </div>
                  }
                  description={t("gloabl.url_must_start_with_https") || "URL must start with https://"}
                >
                  <Input
                    {...field}
                    type="url"
                    placeholder="https://example.com"
                    className="font-mono text-sm"
                  />
                </FormFieldItem>
              )}
            />
          </CardContent>
        </Card>

        <div className="flex items-center gap-3 justify-end">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t("gloabl.cancel")}
            </Button>
          </DialogClose>
          <DialogClose ref={dialogCloseRef} />
          <Button type="submit">{t("gloabl.save")}</Button>
        </div>
      </form>
    </Form>
  );
}

export default SocialMediaForm;
